import { useEffect, useMemo, useState } from "react";

import makeStyles from "@mui/styles/makeStyles";
import {
  Avatar,
  Box,
  Container,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  gameSystemNameFromGameType,
  sortGroupData,
} from "./GroupPlayUtilities";
import GroupHeader from "./components/GroupHeader";
import {
  delay,
  findGetParameter,
  getData,
} from "../../../components/utilities/UtilityFunctions";
import mappingData from "./demoData/mappingData.json";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 650,
  },
  sticky: {
    paddingLeft: "4px",
    position: "sticky",
    left: 0,
    zIndex: 1,
    width: "200px",
    background: theme?.palette?.background.default,
    boxShadow: "5px 2px 5px grey",

    // borderRight: "1px solid",
  },
  scoreUsed: {
    fontWeight: 600,
    textDecoration: "underline",
  },
}));

const headCells = [
  {
    id: "player",
    label: "Player",
    numeric: false,
    sticky: true,
  },
  {
    id: "armyName",
    numeric: false,
    align: "left",
    label: "Faction",
    width: "150px",
  },
  {
    id: "gameType",
    numeric: false,
    align: "center",
    label: "System",
    width: "150px",
  },

  {
    id: "points",
    key: "wcwPoints",
    numeric: true,
    align: "center",
    label: "WCP",
    sortable: true,
  },
  {
    id: "overallPoints",
    key: "wcwOverallPoints",
    numeric: true,
    align: "center",
    label: "Overall",
    sortable: true,
  },
  {
    id: "generalshipPoints",
    key: "wcwBattlePoints",
    numeric: true,
    align: "center",
    label: "Generalship",
    sortable: true,
  },
  {
    id: "paintPoints",
    key: "wcwPaintPoints",
    numeric: true,
    align: "center",
    label: "Paint",
    sortable: true,
  },
  {
    id: "victoryPoints",
    key: "wcwVictoryPoints",
    numeric: true,
    align: "center",
    label: "VPs",
    sortable: true,
  },
  {
    id: "factionPoints",
    key: "wcwFactionPoints",
    numeric: true,
    align: "center",
    label: "Faction",
    sortable: true,
  },
];

function Row({ row, hideCountry, embed, eventGroupId, eventId, gameType }) {
  const classes = useStyles();
  return (
    <TableRow
      sx={{
        borderBottom: "1px solid rgba(81, 81, 81, 1)",
      }}
    >
      <TableCell
        padding="none"
        className={classes.sticky}
        component="th"
        scope="row"
      >
        <span
          style={{
            width: "25px",
            display: "inline-block",
            // display: "inline-flex",
          }}
        >
          {row.place}
        </span>
        {!hideCountry ? (
          <Box
            sx={{
              display: "inline-block",
              mr: 1,
              paddingTop: "2px",
            }}
          >
            <Link
              color="inherit"
              underline="none"
              href={`/world-championships-of-warhammer${
                eventGroupId === "WorldChamps24"
                  ? "-24"
                  : eventGroupId === "WorldChamps23"
                    ? "-23"
                    : ""
              }/players?countryCode=${row.countryCode}&groupName=${
                row.groupName
              }${embed ? `&embed=${embed}` : ""}${
                gameType ? `&gameType=${gameType}` : ""
              }${eventId ? `&eventId=${eventId}` : ""}`}
            >
              <Avatar
                alt="Country"
                src={`${getEnvironmentVariable(
                  "SYSTEM_IMAGES_CLOUDFRONT"
                )}/system-images/countries/${row.countryCode}.svg`}
                sx={{
                  width: 12,
                  height: 12,
                  position: "relative",
                  top: "2px",
                }}
              />
            </Link>
          </Box>
        ) : (
          <></>
        )}

        <span
          style={{
            display: "inline-block",
            position: "relative",
            top: "4px",
            maxWidth: hideCountry ? "calc(100% - 25px)" : "calc(100% - 45px)", // 25 + 16

            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.name}
        </span>
      </TableCell>
      <TableCell
        padding="none"
        sx={{
          ml: 1,
          mr: 1,
          width: "60px",
        }}
        align="center"
      >
        <Link
          href={`/event/${row.eventId}?active_tab=placings`}
          target="_blank"
        >
          <img
            src={`${getEnvironmentVariable(
              "SYSTEM_IMAGES_CLOUDFRONT"
            )}/system-images/gameSystems/gameType${row.gameType}.png`}
            style={{ width: "55px" }}
            alt={gameSystemNameFromGameType(row.gameType)}
          />
        </Link>
      </TableCell>
      <TableCell
        padding="none"
        sx={{
          width: "150px",
          maxWidth: "150px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        align="left"
      >
        {row.armyListId ? (
          <Link href={`/list/${row.armyListId}`} target="_blank">
            {row.armyName || row.parentArmyName}
          </Link>
        ) : (
          row.armyName || row.parentArmyName
        )}
      </TableCell>
      <TableCell padding="none" align="center">
        {row.wcwPoints || 0}
      </TableCell>
      {["Overall", "Battle", "Paint", "Victory", "Faction"].map(
        (pointsType, index) => (
          <TableCell
            padding="none"
            align="center"
            className={row[`wcw${pointsType}Used`] ? classes.scoreUsed : null}
          >
            {row[`wcw${pointsType}Points`] || 0}
          </TableCell>
        )
      )}
    </TableRow>
  );
}

function Header({ orderBy, setOrderBy }) {
  const classes = useStyles();
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="none" className={classes.sticky}>
          <span style={{ width: "25px", display: "inline-flex" }}>#</span>
          <span style={{ display: "inline-flex" }}>Player</span>
        </TableCell>
        <TableCell
          padding="none"
          sx={{
            ml: 1,
            mr: 1,
            width: "60px",
          }}
          align="center"
        >
          System
        </TableCell>
        <TableCell
          padding="none"
          sx={{
            width: "150px",
            maxWidth: "150px",
          }}
          align="left"
        >
          Faction
        </TableCell>
        {headCells
          .filter((el) => el.sortable)
          .map((headCell, index) => {
            let key = headCell.key;
            console.log(key);
            return (
              <TableCell
                key={index}
                align={headCell.align || "left"}
                padding="none"
                sortDirection={orderBy === key ? "desc" : false}
                sx={{ minWidth: "75px" }}
              >
                <TableSortLabel
                  active={orderBy === key}
                  direction={"desc"}
                  onClick={() => {
                    console.log(key);
                    setOrderBy(key);
                  }}
                >
                  {headCell.label}
                  {orderBy === key ? (
                    <Box component="span" sx={visuallyHidden}>
                      sorted descending
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            );
          })}
      </TableRow>
    </TableHead>
  );
}

export default function GroupPlayerDetails({ eventGroupId, label, appRoute }) {
  const [loading, setLoading] = useState(true);
  const _orderBy = findGetParameter("orderBy");

  const [orderBy, setOrderBy] = useState(
    _orderBy ? `${_orderBy}Points` : "wcwPoints"
  );
  const [rows, setRows] = useState([]);
  const embed = appRoute || findGetParameter("embed") === "true";
  const gameType = findGetParameter("gameType");
  const groupName = findGetParameter("groupName");
  const eventId = findGetParameter("eventId");
  const countryCode = findGetParameter("countryCode") || groupName;
  const sortedRows = useMemo(
    () => sortGroupData(rows, orderBy),
    [rows, orderBy]
  );
  const updateData = async () => {
    try {
      let queryStringParameters = {
        limit: 500,
      };
      if (eventId) {
        queryStringParameters.eventId = eventId;
      } else {
        queryStringParameters.eventGroupId = eventGroupId || "WorldChamps23";
      }
      if (groupName || countryCode) {
        queryStringParameters.groupName = groupName || countryCode;
      }
      if (gameType) {
        queryStringParameters.gameType = gameType;
      }
      var apiData = await getData("/players/gwworlds", {
        queryStringParameters: queryStringParameters,
      });
      if (apiData?.data?.length) {
        for (let i = 0; i < apiData.data.length; i++) {
          apiData.data[i].name =
            apiData.data[i].label ||
            `${apiData.data[i].firstName} ${apiData.data[i].lastName}`.trim();
          apiData.data[i].countryCode =
            apiData.data[i].countryCode ||
            mappingData[apiData.data[i].label || apiData.data[i].groupName] ||
            apiData.data[i].label ||
            apiData.data[i].groupName;
        }

        apiData.data = sortGroupData(apiData.data, orderBy);
      }

      setRows(apiData.data);
    } catch (err) {
      postNotification("error", err);
    }
    setLoading(false);

    await delay(60 * 2.5 * 1000);
    updateData();
  };

  useEffect(() => {
    updateData();

    // eslint-disable-next-line
  }, [eventId, gameType]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ maxWidth: "100%" }}>
        <Container maxWidth="lg">
          <GroupHeader label={label} embed={embed} back />
        </Container>
      </Box>
      {countryCode ? (
        <Box
          component="img"
          sx={{
            width: "100%",
          }}
          alt={countryCode}
          src={`${getEnvironmentVariable(
            "SYSTEM_IMAGES_CLOUDFRONT"
          )}/system-images/gw-countries/${countryCode}.png`}
        />
      ) : (
        <></>
      )}
      <Container maxWidth="lg">
        {countryCode ? (
          <></>
        ) : (
          <Typography
            variant="h3"
            sx={{ mt: 1 }}
            gutterBottom
            textAlign="center"
          >
            Player Leaders
          </Typography>
        )}

        <TableContainer style={{ maxWidth: "100%" }}>
          <Table
            aria-label="simple table"
            sx={{
              tableLayout: "fixed",
              minWidth: 650,
              "& .MuiTableCell-root": {
                fontSize: "12px",
              },
            }}
          >
            <colgroup>
              <col style={{ width: "200px" }} />
              <col style={{ width: "60px" }} />
              <col style={{ width: "150px" }} />

              <col style={{ width: "75px" }} />

              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
              <col style={{ width: "75px" }} />
            </colgroup>
            <Header orderBy={orderBy} setOrderBy={setOrderBy} />
            <TableBody>
              {sortedRows.map((row, index) => (
                <Row
                  key={`${index}-${row.name}`}
                  row={row}
                  hideCountry={Boolean(countryCode)}
                  embed={embed}
                  gameType={gameType}
                  eventId={eventId}
                  eventGroupId={eventGroupId}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {loading && <ModalLoadingFull />}
    </Box>
  );
}
